import React from 'react';
import { Link } from 'react-router-dom';
import WiringCanvas from '../components/WiringCanvas';
import Typewriter from '../components/Typewriter';
import Reveal from '../components/Reveal';
import SpotlightCard from '../components/SpotlightCard';
import Counter from '../components/Counter';
import ProjectArt from '../components/ProjectArt';
import { ArrowRight, ServiceIcon } from '../components/Icons';
import usePageTitle from '../hooks/usePageTitle';
import { PROCESS, PROJECTS, SERVICES, STATS, STRENGTHS } from '../data/content';
import './HomePage.css';

const PHRASES = ['notices every detail.', 'thinks in systems.', 'builds what others miss.', 'ships accessible by default.'];

const TerminalCard = () => (
  <div className="terminal" aria-hidden="true">
    <div className="terminal-bar">
      <span />
      <span />
      <span />
      <em>studio.config.js</em>
    </div>
    <pre className="terminal-body">
      <code>
        <span className="t-line">
          <span className="t-com">{'// how we build'}</span>
        </span>
        <span className="t-line">
          <span className="t-key">const</span> team = <span className="t-fn">hire</span>({'{'} wiring:{' '}
          <span className="t-str">'different'</span> {'}'});
        </span>
        <span className="t-line">
          <span className="t-key">const</span> product = <span className="t-fn">build</span>(team, {'{'}
        </span>
        <span className="t-line">
          {'  '}accessible: <span className="t-key">true</span>,
        </span>
        <span className="t-line">
          {'  '}meetings: <span className="t-str">'with an agenda'</span>,
        </span>
        <span className="t-line">
          {'  '}details: <span className="t-str">'all of them'</span>,
        </span>
        <span className="t-line">{'});'}</span>
        <span className="t-line t-out">
          <span className="t-ok">✓</span> shipped · 0 accessibility violations
        </span>
      </code>
    </pre>
  </div>
);

const HomePage = () => {
  usePageTitle(null);
  const featured = PROJECTS.slice(0, 3);

  return (
    <div className="home-page">
      {/* Hero */}
      <section className="hero">
        <WiringCanvas />
        <div className="hero-glow" aria-hidden="true" />
        <div className="container hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">Neurodiverse software studio</p>
            <h1 className="hero-title">
              Different wiring.
              <br />
              <span className="hero-title-accent">Brilliant software.</span>
            </h1>
            <p className="hero-sub">
              We're a neurodiverse team that <Typewriter phrases={PHRASES} />
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="btn">
                Start a project <ArrowRight />
              </Link>
              <Link to="/portfolio" className="btn btn-ghost">
                See our work
              </Link>
            </div>
          </div>
          <TerminalCard />
        </div>
        <div className="scroll-cue" aria-hidden="true">
          <span />
        </div>
      </section>

      {/* Strengths marquee */}
      <div className="marquee">
        <ul className="sr-only">
          {STRENGTHS.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <div className="marquee-track" aria-hidden="true">
          {[...STRENGTHS, ...STRENGTHS].map((s, i) => (
            <span key={i} className="marquee-item">
              {s}
              <svg width="14" height="14" viewBox="0 0 14 14">
                <path d="M8 0L3 8h4l-1 6 5-8H7z" fill="currentColor" />
              </svg>
            </span>
          ))}
        </div>
      </div>

      {/* Mission + stats */}
      <section className="section">
        <div className="container mission-grid">
          <Reveal className="mission-copy">
            <p className="eyebrow">Our mission</p>
            <h2 className="section-title">
              Neurodiversity is a <span className="highlight">competitive advantage.</span>
            </h2>
            <p className="lead">
              At Autistic Dev, our team brings unique perspectives, attention to detail and innovative problem-solving
              to every project. We're committed to creating accessible, user-friendly digital solutions while
              championing inclusion in the tech industry.
            </p>
            <Link to="/about" className="text-link mission-link">
              Meet the team <ArrowRight />
            </Link>
          </Reveal>
          <div className="stats-grid">
            {STATS.map((stat, i) => (
              <Reveal key={stat.label} delay={i * 90} className="stat card">
                <div className="stat-value">
                  <Counter value={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
                </div>
                <p>{stat.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section section-alt">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">What we do</p>
            <h2 className="section-title">Built with precision. Designed for everyone.</h2>
          </Reveal>
          <div className="services-grid">
            {SERVICES.map((service, i) => (
              <Reveal key={service.id} delay={i * 80}>
                <SpotlightCard className="service-card">
                  <span className="service-num">0{i + 1}</span>
                  <div className="service-icon">
                    <ServiceIcon name={service.icon} />
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </SpotlightCard>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center section-more">
            <Link to="/services" className="btn btn-ghost">
              Explore services <ArrowRight />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Process */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head center">
            <p className="eyebrow">How we work</p>
            <h2 className="section-title">Predictable by design.</h2>
            <p className="lead">
              Clear expectations, written-first communication and no surprises. It turns out what works for us works
              brilliantly for clients too.
            </p>
          </Reveal>
          <ol className="process">
            {PROCESS.map((step, i) => (
              <Reveal as="li" key={step.title} delay={i * 140} className="process-step">
                <span className="process-node" aria-hidden="true">
                  {i + 1}
                </span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Featured work */}
      <section className="section section-alt">
        <div className="container">
          <Reveal className="section-head featured-head">
            <div>
              <p className="eyebrow">Selected work</p>
              <h2 className="section-title">Products that include everyone.</h2>
            </div>
            <Link to="/portfolio" className="text-link">
              All projects <ArrowRight />
            </Link>
          </Reveal>
          <div className="featured-grid">
            {featured.map((project, i) => (
              <Reveal key={project.id} delay={i * 100}>
                <Link to="/portfolio" className="featured-card card">
                  <ProjectArt seed={project.id} hue={project.hue} />
                  <div className="featured-body">
                    <span className="tag">{project.category}</span>
                    <h3>{project.title}</h3>
                    <p>{project.description}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section cta">
        <div className="container">
          <Reveal className="cta-inner">
            <h2>
              Let's build something <span className="highlight">brilliantly different.</span>
            </h2>
            <p className="lead">Tell us about your project. We'll reply in writing, with no pressure and no jargon.</p>
            <Link to="/contact" className="btn">
              Get in touch <ArrowRight />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
